<template>
    <zen-dropdown-menu
        placement="bottom-end"
        menu-class="w-84 max-w-[calc(100vw-2rem)] rounded-md border border-taupe-200 bg-white shadow-lg"
    >
        <template
            v-slot:trigger="{
                toggle,
                setReference: setMenuReference,
                ariaAttrs: menuAriaAttrs,
            }"
        >
            <zen-tooltip
                content="Copy environment keys"
                placement="bottom"
                v-slot="{
                    setReference: setTooltipReference,
                    ariaAttrs: tooltipAriaAttrs,
                    show,
                    hide,
                }"
            >
                <button
                    type="button"
                    :ref="
                        (el) => {
                            setMenuReference(el)
                            setTooltipReference(el)
                        }
                    "
                    v-bind="{ ...menuAriaAttrs, ...tooltipAriaAttrs }"
                    data-testid="environment-keys-trigger"
                    class="cursor-pointer rounded-full p-1 text-taupe-500 hover:bg-taupe-200 hover:text-taupe-800"
                    @click="toggle"
                    @mouseenter="show"
                    @mouseleave="hide"
                    @focus="show"
                    @blur="hide"
                >
                    <zen-icon-token css-classes="h-5 w-5" />
                </button>
            </zen-tooltip>
        </template>

        <template v-slot:default>
            <div data-testid="environment-keys-menu">
                <div
                    class="border-b border-taupe-100 px-3 py-2 text-xs font-medium uppercase tracking-wide text-taupe-500"
                >
                    Environment Keys
                </div>

                <div class="py-1">
                    <div
                        v-for="entry in environmentKeyEntries"
                        :key="entry.id"
                        class="px-3 py-2"
                    >
                        <div
                            class="rounded-md border border-taupe-200 bg-white p-2.5 shadow-xs"
                        >
                            <div
                                class="flex items-center justify-between gap-2"
                            >
                                <div
                                    class="text-[11px] font-medium uppercase tracking-[0.08em] text-taupe-500"
                                >
                                    {{ entry.label }}
                                </div>

                                <div
                                    v-if="copiedEntryId === entry.id"
                                    :data-testid="`environment-key-copied-${entry.id}`"
                                    class="rounded-sm border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.08em] text-emerald-700"
                                >
                                    Copied
                                </div>
                            </div>

                            <button
                                v-if="entry.available"
                                :data-testid="`environment-key-copy-${entry.id}`"
                                class="mt-2 w-full cursor-pointer rounded-md border border-sky-200 bg-sky-50 px-2 py-1.5 text-left font-mono text-sm leading-6 text-sky-800 whitespace-nowrap overflow-x-auto hover:bg-sky-100 hover:text-sky-900"
                                v-clipboard:copy="entry.value"
                                v-clipboard:success="
                                    buildCopySuccessHandler(
                                        entry.id,
                                        entry.label
                                    )
                                "
                                v-clipboard:error="
                                    buildCopyErrorHandler(entry.label)
                                "
                            >
                                {{ entry.value }}
                            </button>

                            <div
                                v-else
                                class="mt-2 rounded-md border border-dashed border-taupe-200 bg-taupe-50 px-2 py-1.5 text-sm italic text-taupe-400"
                            >
                                Unavailable
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </zen-dropdown-menu>
</template>

<script>
import {
    getEnvironmentKeyCopyErrorMessage,
    getEnvironmentKeyCopySuccessMessage,
    getMultiEnvironmentKeyEntries,
} from '@/utils/environment-keys'
import { errorMessage, infoMessage } from '@/utils/notifications'

export default {
    name: 'EnvironmentKeysButton',
    props: {
        environment: {
            type: Object,
            required: true,
        },
        project: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            copiedEntryId: null,
            copiedStateTimer: null,
        }
    },
    computed: {
        environmentKeyEntries() {
            return getMultiEnvironmentKeyEntries(this.environment)
        },
    },
    beforeUnmount() {
        if (this.copiedStateTimer !== null) {
            window.clearTimeout(this.copiedStateTimer)
            this.copiedStateTimer = null
        }
    },
    methods: {
        buildCopyErrorHandler(label) {
            return () => {
                errorMessage(getEnvironmentKeyCopyErrorMessage(label))
            }
        },
        buildCopySuccessHandler(entryIdOrLabel, maybeLabel) {
            return () => {
                const entryId = maybeLabel === undefined ? null : entryIdOrLabel
                const label =
                    maybeLabel === undefined ? entryIdOrLabel : maybeLabel

                this.copiedEntryId = entryId
                if (this.copiedStateTimer !== null) {
                    window.clearTimeout(this.copiedStateTimer)
                }
                this.copiedStateTimer = window.setTimeout(() => {
                    this.copiedEntryId = null
                    this.copiedStateTimer = null
                }, 1600)

                infoMessage(getEnvironmentKeyCopySuccessMessage(label))
            }
        },
    },
}
</script>
